// src/components/Gallery.js
import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Alert } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useContent } from '../hooks/useContent'
import FloatingActionButton from './FloatingActionButton'
import Footer from './footer'
import './Gallery.css'

const Gallery = () => {
  const navigate = useNavigate()
  const { content, loading, error } = useContent('gallery')
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Extract content with fallbacks
  const heroContent = content?.hero || {
    title: 'Our Gallery',
    subtitle: 'Moments & Memories from Ampthilldowns',
    backgroundImage: '',
  }
  const introContent = content?.intro || {
    title: 'Explore Our Collection',
    description:
      'A visual journey through our resort, farm, and guest experiences',
  }
  const mediaItems = content?.mediaItems || []
  const ctaContent = content?.cta || {
    title: 'Create Your Own Memories',
    description: 'Book your stay and become part of our story',
    primaryButtonText: 'Book Your Stay',
    secondaryButtonText: 'Contact Us',
  }

  const defaultHeroImage =
    'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'

  // Track hero image load to avoid overlay flash
  const [heroImage, setHeroImage] = useState('')
  const [heroLoaded, setHeroLoaded] = useState(false)

  useEffect(() => {
    if (loading) return
    let cancelled = false
    const desired = (heroContent?.backgroundImage || defaultHeroImage || '').trim()

    const load = (src) => {
      setHeroLoaded(false)
      const img = new Image()
      img.onload = () => {
        if (cancelled) return
        setHeroImage(src)
        setHeroLoaded(true)
      }
      img.onerror = () => {
        if (cancelled) return
        if (src !== defaultHeroImage) {
          load(defaultHeroImage)
        } else {
          setHeroImage(defaultHeroImage)
          setHeroLoaded(true)
        }
      }
      img.src = src
    }

    if (desired) {
      load(desired)
    } else {
      load(defaultHeroImage)
    }

    return () => {
      cancelled = true
    }
  }, [loading, heroContent?.backgroundImage])

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  const closeLightbox = () => {
    setLightboxOpen(false)
  }

  const navigateNext = () => {
    setLightboxIndex((prev) => (prev + 1) % mediaItems.length)
  }

  const navigatePrev = () => {
    setLightboxIndex(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length
    )
  }

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowRight') navigateNext()
      if (e.key === 'ArrowLeft') navigatePrev()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, lightboxIndex, mediaItems.length])

  // Removed loading spinner: render page immediately with fallback content.

  // Show error state
  if (error) {
    return (
      <div className='gallery-page'>
        <Alert variant='danger' className='text-center mb-0'>
          <strong>Content Loading Error:</strong> {error}
          <p className='mt-2'>Please try refreshing the page.</p>
        </Alert>
      </div>
    )
  }

  return (
    <div className='gallery-page'>
      {/* Hero Section */}
      <section className='hero-section'>
        <div
          className='hero-image'
          style={{
            backgroundImage: heroImage ? `url(${heroImage})` : 'none',
          }}
        >
          <div className={`hero-overlay ${heroLoaded ? 'is-loaded' : ''}`}>
            <div className='hero-content'>
              <h1 className='hero-title'>{heroContent.title}</h1>
              <p className='hero-subtitle'>{heroContent.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className='intro-section py-5'>
        <Container>
          <div className='text-center mb-5'>
            <h2 className='section-head'>{introContent.title}</h2>
            <p className='lead'>{introContent.description}</p>
          </div>
        </Container>
      </section>

      {/* Gallery Grid */}
      <section className='gallery-grid-section py-5'>
        <Container>
          {mediaItems.length === 0 ? (
            <div className='text-center py-5'>
              <p className='text-muted'>
                No media items yet. Add some in the admin panel!
              </p>
            </div>
          ) : (
            <div className='gallery-grid'>
              {mediaItems.map((item, index) => (
                <div
                  key={index}
                  className='gallery-item'
                  onClick={() => openLightbox(index)}
                >
                  {item.type === 'video' ? (
                    <div className='gallery-video-wrapper'>
                      <video
                        src={item.url}
                        className='gallery-media'
                        muted
                        playsInline
                      />
                      <div className='video-play-overlay'>
                        <div className='play-icon'>▶</div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.alt || `Gallery item ${index + 1}`}
                      className='gallery-media'
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* CTA Section */}
      <section className='sectionCta py-5 text-center'>
        <Container>
          <h2 className='section-head'>{ctaContent.title}</h2>
          <p className='cta-text mb-4'>{ctaContent.description}</p>
          <div className='gallery-cta-buttons'>
            <button className='book-btn' onClick={() => navigate('/stays')}>
              {ctaContent.primaryButtonText}
            </button>
            <button className='book-btn' onClick={() => navigate('/contact')}>
              {ctaContent.secondaryButtonText}
            </button>
          </div>
        </Container>
      </section>

      {/* Lightbox */}
      {lightboxOpen && mediaItems.length > 0 && (
        <div className='lightbox-overlay' onClick={closeLightbox}>
          <button className='lightbox-close' onClick={closeLightbox}>
            ×
          </button>

          {mediaItems.length > 1 && (
            <>
              <button
                className='lightbox-arrow lightbox-prev'
                onClick={(e) => {
                  e.stopPropagation()
                  navigatePrev()
                }}
              >
                ‹
              </button>
              <button
                className='lightbox-arrow lightbox-next'
                onClick={(e) => {
                  e.stopPropagation()
                  navigateNext()
                }}
              >
                ›
              </button>
            </>
          )}

          <div
            className='lightbox-content'
            onClick={(e) => e.stopPropagation()}
          >
            {mediaItems[lightboxIndex]?.type === 'video' ? (
              <video
                src={mediaItems[lightboxIndex]?.url}
                className='lightbox-media'
                controls
                autoPlay
                loop
              />
            ) : (
              <img
                src={mediaItems[lightboxIndex]?.url}
                alt={
                  mediaItems[lightboxIndex]?.alt ||
                  `Gallery item ${lightboxIndex + 1}`
                }
                className='lightbox-media'
              />
            )}
          </div>

          <div className='lightbox-counter'>
            {lightboxIndex + 1} / {mediaItems.length}
          </div>
        </div>
      )}

      <FloatingActionButton />
      <Footer />
    </div>
  )
}

export default Gallery
